import { createWebHistory, createRouter } from 'vue-router';
import ClientAdminGroupSetting from '@/components/ClientAdminGroupSetting';
import ClientAdminLayout from '@/components/ClientAdminLayout';
import ClientAdminOperationManagement from '@/components/ClientAdminOperationManagement';
import ClientAdminRiskCategoryManagement from '@/components/ClientAdminRiskCategoryManagement';
import ClientAdminRiskMatrixManagement from '@/components/ClientAdminRiskMatrixManagement';
import ClientAdminStudentManagement from '@/components/ClientAdminStudentManagement';
import EntityManagement from '@/components/EntityManagement';
import ExcursionBuilder from '@/components/ExcursionBuilder';
import SafeTripBuilder from '@/components/SafeTripBuilder';
import SupportFeedbackLayout from '@/components/SupportFeedbackLayout';
import AdministrationManagement from '@/components/AdministrationManagement';
import LoginCallback from '@/components/LoginCallback';
import AhaFeedbackLogin from '@/components/AhaFeedbackLogin';
import store from '@/store';
import waitForAuthReady from '@/router/waitForAuthReady';
import * as types from '@/store/mutationTypes';

const HUB_REDIRECT_EXCLUDED_PATHS = [
  '/login',
  '/login/callback',
  '/login/callback/hub',
  '/login/callback/idp',
  '/logout/callback/hub'
];
async function beforeEachRoute(to, from, next) {
  if (to.name === 'SafeTripBuilderExistingRecord' && to.params && to.params.id === '0') {
    return next('/excursions/new');
  }
  if (to.name === 'ExcursionBuilderExistingRecord' && to.params && to.params.id === '0') {
    return next('/excursions/builder/new');
  }
  if (to.path === '/' || !to.name || !to.matched.length) {
    return next('/excursions');
  }

  const isVendorAuth = !!to.query['vendor-sid'] && !!to.query['oauth-signature'];
  if (isVendorAuth) {
    return next();
  }

  const isLoginNav = to.name === 'login' || to.path === '/login';
  const hasCrossTenantSession = store.getters['authorisation/crossTenantSessionEnabled'];
  if (isLoginNav && hasCrossTenantSession) {
    const fallback = localStorage.getItem('redirect') || '/excursions';
    if (from.fullPath && from.fullPath !== '/' && from.name) return next(false); // cancel; stay on the page CSDS tried to evict from

    return next(fallback);
  }

  const isFirstLoad = from.name === undefined;
  const isProtectedFirstLoad = isFirstLoad && !HUB_REDIRECT_EXCLUDED_PATHS.includes(to.path);

  if (isProtectedFirstLoad) {
    localStorage.setItem('redirect', to.path);
    store.commit(types.APP_SET_NAVIGATION_BLOCKED, true);

    await store.dispatch('cross-tenant/fetchCrossTenantSessionInfo');
    if (store.state['cross-tenant'].crossTenantSession === true) {
      try {
        await store.dispatch('hub/getHubConfigByHostname');
      } catch (err) {
        console.warn('[cross-tenant] hub config unresolved, continuing without hub features', err);
      }
      store.commit(types.APP_SET_NAVIGATION_BLOCKED, false);
      store.commit(types.AUTHORISATION_SET_IS_AUTHENTICATED, true);
      localStorage.setItem('redirect', to.path);
      return next();
    }

    const ready = await waitForAuthReady(store, 5000);
    store.commit(types.APP_SET_NAVIGATION_BLOCKED, false);

    if (!ready) {
      if (
        !store.getters['authorisation/hubEnabled'] &&
        !store.getters['authorisation/auth0Enabled']
      ) {
        localStorage.setItem('idp_redirect', to.path);
      }
      return next('/login');
    }
  }

  return next();
}

function routerComposer() {
  const router = createRouter({
    history: createWebHistory(),
    routes: [
      {
        path: '/',
        redirect: '/excursions'
      },
      {
        path: '/login/callback/idp',
        name: 'LoginCallback',
        component: LoginCallback
      },
      {
        path: '/excursions/builder/new',
        name: 'ExcursionBuilderNewRecord',
        component: ExcursionBuilder
      },
      {
        path: '/excursions/builder/:id',
        name: 'ExcursionBuilderExistingRecord',
        component: ExcursionBuilder
      },
      {
        path: '/excursions/new',
        name: 'SafeTripBuilderNewRecord',
        component: SafeTripBuilder
      },
      {
        path: '/excursions',
        name: 'Excursions',
        component: EntityManagement
      },
      {
        path: '/excursions/templates',
        name: 'Templates',
        component: EntityManagement
      },
      {
        path: '/admin/group',
        name: 'ClientAdminGroupSetting',
        component: ClientAdminGroupSetting
      },
      {
        path: '/admin/student-management',
        name: 'ClientAdminStudentManagement',
        component: ClientAdminStudentManagement
      },
      {
        path: '/admin/risk-management',
        name: 'ClientAdminRiskMatrixManagement',
        component: ClientAdminRiskMatrixManagement
      },
      {
        path: '/admin/risk-category-management',
        name: 'ClientAdminRiskCategoryManagement',
        component: ClientAdminRiskCategoryManagement
      },
      {
        path: '/admin/operation-management',
        name: 'ClientAdminOperationManagement',
        component: ClientAdminOperationManagement
      },
      {
        path: '/admin',
        name: 'ClientAdminLayout',
        component: ClientAdminLayout
      },
      {
        path: '/feedback',
        name: 'SupportFeedbackLayout',
        component: SupportFeedbackLayout
      },
      {
        path: '/admin/clients/users',
        name: 'SafeTripBuilderClientsUsersList',
        component: AdministrationManagement
      },
      {
        path: '/admin/clients/new',
        name: 'SafeTripBuilderClientNewRecord',
        component: AdministrationManagement
      },
      {
        path: '/admin/clients',
        name: 'SafeTripBuilderClientsList',
        component: AdministrationManagement
      },
      {
        path: '/admin/users',
        name: 'SafeTripBuilderUsersList',
        component: AdministrationManagement
      },
      {
        path: '/admin/clients/:id',
        name: 'SafeTripBuilderClientExistingRecord',
        component: AdministrationManagement
      },
      {
        path: '/excursions/:id',
        name: 'SafeTripBuilderExistingRecord',
        component: SafeTripBuilder
      },
      {
        path: '/aha/login',
        name: 'AhaFeedbackLogin',
        component: AhaFeedbackLogin
      }
    ]
  });

  router.beforeEach(beforeEachRoute);

  return router;
}

export default {
  beforeEachRoute,
  routerComposer
};
